import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SessionTypeBadge } from "./session-type-badge";
import { IconButton } from "./icon-button";
import {
  fileCodeIcon,
  presentationIcon,
  videoIcon,
} from "@/components/ui/static-lucide";
import { sessionSlug } from "@/util/talks";
import { SessionType } from "@/data/core";
import Link from "next/link";
import { ExternalLink } from "@/components/ui/external-link";
import { formatDateMedium } from "@/util/formatters";
import { cn } from "@/lib/utils";

interface SessionItem {
  id: string;
  title: string;
  type: SessionType;
  slotLabel?: string | null;
  slidesUrl?: string | null;
  codeUrl?: string | null;
  recordingUrl?: string | null;
}

interface Event {
  event: string;
  eventWebsite: string | null;
  date: string;
  startDateTimestamp: number;
  endDate: string | null;
  endDateTimestamp: number | null;
  location: string | null;
  sessions: SessionItem[];
  hasMaterials: boolean;
}

export const EventsTable = React.memo(function EventsTable({
  events,
  idPrefix,
}: {
  events: Event[];
  idPrefix?: string;
}) {
  // Optimization: use pre-calculated hasMaterials flag
  // This avoids O(N) iteration over all sessions on every render
  const hasMaterials = React.useMemo(
    () => events.some((event) => event.hasMaterials),
    [events],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="whitespace-nowrap">Start</TableHead>
          <TableHead className="whitespace-nowrap">End</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Sessions</TableHead>
          {hasMaterials && (
            <TableHead className="text-right whitespace-nowrap">
              Materials
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={hasMaterials ? 6 : 5}
              className="text-muted-foreground h-24 text-center"
            >
              No sessions match your current filters.
            </TableCell>
          </TableRow>
        )}
        {events.map((event, index) => (
          <TableRow
            data-testid="events-table-row"
            key={`${event.event}__${event.date}`}
            id={idPrefix ? `${idPrefix}-${index}` : undefined}
            tabIndex={idPrefix ? -1 : undefined}
            className={cn(idPrefix && "focus:bg-muted/50 focus:outline-none")}
          >
            <TableCell className="whitespace-nowrap">
              {formatDateMedium(event.startDateTimestamp)}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {formatDateMedium(event.endDateTimestamp)}
            </TableCell>
            <TableCell className="text-pretty">
              {event.eventWebsite ? (
                <ExternalLink
                  className="underline underline-offset-4"
                  href={event.eventWebsite}
                  aria-label={event.event}
                >
                  {event.event}
                </ExternalLink>
              ) : (
                event.event
              )}
            </TableCell>
            <TableCell data-testid="events-table-location">
              {event.location ?? "Online"}
            </TableCell>
            {/* Sessions and their materials share one cell (spanning the
                Materials column) so each buttons group sits in the same flex
                row as its session and stays vertically centred against it.
                As separate per-column stacks, the two columns drifted apart
                as soon as a title wrapped to more lines than its buttons. */}
            <TableCell colSpan={hasMaterials ? 2 : 1}>
              <div className="flex flex-col gap-2">
                {event.sessions.map((session) => (
                  <div
                    key={`session-${session.id}`}
                    data-testid="events-table-session"
                    className="flex items-center gap-2 py-1 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      <SessionTypeBadge
                        type={session.type}
                        variant="secondary"
                      />
                      <div className="min-w-0 flex-1">
                        <Link
                          className="wrap-break-word underline underline-offset-4"
                          href={`/session/${sessionSlug(session.title)}`}
                          aria-label={`${session.type}: ${session.title}${
                            session.slotLabel ? ` (${session.slotLabel})` : ""
                          }`}
                        >
                          {session.title}
                        </Link>
                        {session.slotLabel && (
                          <div className="text-muted-foreground mt-0.5 text-sm/snug">
                            {session.slotLabel}
                          </div>
                        )}
                      </div>
                    </div>
                    {hasMaterials &&
                      (session.slidesUrl ||
                        session.codeUrl ||
                        session.recordingUrl) && (
                        <div
                          data-testid="events-table-session-materials"
                          className="flex shrink-0 items-center justify-end gap-2"
                        >
                          {session.slidesUrl && (
                            <IconButton
                              href={session.slidesUrl}
                              ariaLabel={`Slides for ${session.title} at ${event.event}`}
                              tooltipText="Slides"
                              icon={presentationIcon}
                              data-testid="slides-icon-button"
                            />
                          )}
                          {session.codeUrl && (
                            <IconButton
                              href={session.codeUrl}
                              ariaLabel={`Code for ${session.title} at ${event.event}`}
                              tooltipText="Code"
                              icon={fileCodeIcon}
                              data-testid="code-icon-button"
                            />
                          )}
                          {session.recordingUrl && (
                            <IconButton
                              href={session.recordingUrl}
                              ariaLabel={`Recording for ${session.title} at ${event.event}`}
                              tooltipText="Recording"
                              icon={videoIcon}
                              data-testid="recording-icon-button"
                            />
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});
